
'use client';
import SupportUs from '@/components/common/SupportUs';

import Header from '@/components/common/Header';
import AdblockDetectOverlay from '@/components/common/AdblockDetectOverlay';
import MonetagAds from '@/components/common/MonetagAds';

import Footer from './components/Footer';
import HeroSlider from './components/HeroSlider';
import NewsTicker from './components/NewsTicker';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { db, requireDb } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Article } from '@/types/article';
import { useDarkMode } from '@/lib/DarkModeContext';
import { getArticleUrl } from '@/lib/slugs';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

export default function HomePage() {
    const [articles, setArticles] = useState<Article[]>([]);
    const [loading, setLoading] = useState(true);
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();

    const categoryTranslations: Record<string, string> = {
        'Vijesti': 'News',
        'Naoru\u017eanje': 'Armament', 'Municija': 'Ammunition',
        'Namjenska preduze\u0107a': 'Defense Enterprises', 'Vojna istorija': 'Military History',
        'Kopnene snage': 'Ground Forces', 'Vazduhoplovstvo i PVO': 'Air Force & Air Defense',
        'Mornarica': 'Navy', 'Strate\u0161ke snage': 'Strategic Forces',
        'Naoru\u017eanje Tre\u0107eg rajha': 'Third Reich Armaments', 'Naoru\u017eanje biv\u0161e JNA': 'Former JNA Armaments',
        'Savremeno naoru\u017eanje': 'Modern Armaments', 'Konstrukcija naoru\u017eanja': 'Weapon Construction',
        'Tenkovi i oklopna vozila': 'Tanks & Armored Vehicles', 'Artiljerija': 'Artillery',
        'Pje\u0161adijska municija': 'Infantry Ammunition', 'Minobaca\u010dka municija': 'Mortar Ammunition',
        'Klasi\u010dna artiljerijska municija': 'Classic Artillery Ammunition',
        'Raketna artiljerijska municija': 'Rocket Artillery Ammunition',
        'Vodjene rakete': 'Guided Missiles', 'Ru\u010dne bombe': 'Hand Grenades',
        'Mine za ru\u010dne baca\u010de': 'Grenades for Handheld Launchers',
        'Municija za bestrzajne topove': 'Recoilless Cannon Ammunition',
        'Balistika': 'Ballistics', 'Savremena municija': 'Modern Ammunition',
        'Eksplozivne materije': 'Explosives', 'Elementi municije': 'Ammunition Elements',
        'Pirotehnika': 'Pyrotechnics', 'Upalja\u010di': 'Fuzes',
        'Istorija razvoja': 'History of Development', 'Proizvodni kapaciteti': 'Production Capacity',
        'Stari jugoslovenski projekti': 'Old Yugoslav Projects',
        'Modernizacija i izvozni program': 'Modernization & Export Program',
        'Vojske kroz vijekove': 'Armies Through the Ages',
        'I svjetski rat': 'World War I', 'II svjetski rat': 'World War II',
        'Hladni rat': 'Cold War', 'Poznati konstruktori': 'Famous Designers',
        'Nekategorisano': 'Uncategorized',
    };

    const translateCat = (name: string) =>
        siteLang === 'en' ? (categoryTranslations[name] ?? name) : name;

    // Filter by selected site language
    const displayedArticles = articles.filter(a =>
        siteLang === 'en' ? a.lang === 'en' : (a.lang === 'sr' || !a.lang)
    );

    useEffect(() => {
        const loadArticles = async () => {
            try {
                const querySnapshot = await getDocs(collection(requireDb(), 'clanci'));

                const allArticles: Article[] = [];
                querySnapshot.docs.forEach((doc) => {
                    allArticles.push({
                        ...doc.data(),
                        docId: doc.id
                    } as Article);
                });

                allArticles.sort((a: Article, b: Article) => (b.id || 0) - (a.id || 0));
                setArticles(allArticles);
            } catch {
                // Error loading articles
            } finally {
                setLoading(false);
            }
        };

        loadArticles();
    }, []);

    return (
        <main
            className="min-h-screen flex flex-col"
            style={{ backgroundColor: darkMode ? '#111827' : '#ffffff' }}
        >
            <AdblockDetectOverlay />
            <Header />
            <MonetagAds />

            {/* News Ticker - Latest 3 Articles */}
            {!loading && displayedArticles.length > 0 && (
                <NewsTicker articles={displayedArticles} />
            )}

            {/* Hero Slider - Latest 3 Articles */}
            {!loading && displayedArticles.length > 0 && (
                <HeroSlider articles={displayedArticles} />
            )}



            {/* Latest News Section */}
            <section className="w-full pt-8 pb-16 px-4 lg:px-[calc(1rem+180px)]">
                {/* Section Header */}
                <div className="mb-6 flex items-center space-x-3 w-full">
                    <div className="px-4 py-2 w-full" style={{ backgroundColor: 'rgb(27, 34, 49)', borderRadius: '5px' }}>
                        <span className="text-white font-bold text-base">★&nbsp;&nbsp;&nbsp;{siteLang === 'en' ? 'LATEST' : 'NAJNOVIJE'}</span>
                    </div>
                </div>

                {/* News Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        <div className="col-span-full text-center py-12">
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>{siteLang === 'en' ? 'Loading articles...' : 'U\u010ditavanje \u010dlanaka...'}</p>
                        </div>
                    ) : displayedArticles.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>{siteLang === 'en' ? 'No articles published' : 'Nema objavljenih \u010dlanaka'}</p>
                        </div>
                    ) : (
                        displayedArticles.slice(0, 6).map((article) => (
                            <Link
                                key={article.id}
                                href={getArticleUrl(article)}
                                className="group"
                                onClick={e => {
                                    e.preventDefault();
                                    const lastPop = localStorage.getItem('monetag_direct_last');
                                    const now = Date.now();
                                    if (!lastPop || now - parseInt(lastPop, 10) > 120000) {
                                        window.open('https://omg10.com/4/10759841', '_blank', 'noopener');
                                        localStorage.setItem('monetag_direct_last', now.toString());
                                    }
                                    // Navigate to article after (ad may or may not open)
                                    window.location.href = getArticleUrl(article);
                                }}
                            >
                                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} overflow-hidden shadow-md hover:shadow-lg transition-shadow rounded-[8px]`}>
                                    {/* Image */}
                                    {article.naslovna_slika && (
                                        <div className="relative overflow-hidden bg-gray-200 h-56 sm:h-[220px] lg:h-72 rounded-t-[8px] flex items-center justify-center">
                                            <img
                                                src={article.naslovna_slika}
                                                alt={article.naslov}
                                                className="w-full h-full object-fill transition-transform"
                                            />
                                        </div>
                                    )}

                                    {/* Content */}
                                    <div className="px-5 py-3" style={{ paddingLeft: '0' }}>
                                        {(() => {
                                            const label = article.kategorija === 'Vijesti' ? article.kategorija : (article.podpodkategorija || article.podkategorija || article.kategorija);
                                            return label ? (
                                                <span className={`text-xs font-bold ${darkMode ? 'text-gray-400' : 'text-gray-700'} uppercase tracking-wide`}>{translateCat(label)}</span>
                                            ) : null;
                                        })()}

                                        <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mt-2 mb-1 line-clamp-3 group-hover:text-blue-600`} style={{ lineHeight: '1.2' }}>
                                            {article.naslov}
                                        </h3>
                                        <p className={`text-base font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 mb-4`}>{article.kratak_opis}</p>
                                        <div className={`text-[11px] sm:text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-500'} tracking-wide`}>
                                            {article.autor} / {article.datum}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))
                    )}
                </div>
            </section>

            {/* Support Us Donation Section */}
            <SupportUs />
            {/* Footer */}
            <div className="mt-auto">
                <Footer />
            </div>
        </main>
    );
}
